import type { EventoIncidente } from '@/data/types';
import { ROTULO_SEVERIDADE, ROTULO_STATUS_INCIDENTE } from '@/lib/incidentes';

/**
 * Tradução de `eventos[].detalhe` (formato por tipo, ver `docs/CONTRATO.md`)
 * para texto de linha do tempo. Valor fora do esperado nunca quebra: cai num
 * texto genérico em vez de `[object Object]`.
 */

export interface MudancaDeCampo {
  campo: string;
  de: string;
  para: string;
}

export interface EventoDescrito {
  resumo: string;
  /** Linhas "campo: antes → depois" (edição). */
  mudancas: MudancaDeCampo[];
  /** Linha extra de contexto (ex.: versões do registro). */
  complemento: string | null;
}

const ROTULO_CAMPO: Record<string, string> = {
  titulo: 'Título',
  descricao: 'Descrição',
  severidade: 'Severidade',
  link: 'Link',
  corrigido_em: 'Corrigido em',
};

const VAZIO = '(vazio)';

function texto(valor: unknown): string {
  if (typeof valor === 'string') return valor.trim() === '' ? VAZIO : valor;
  if (valor === null || valor === undefined) return VAZIO;
  if (typeof valor === 'number' || typeof valor === 'boolean') return String(valor);
  return JSON.stringify(valor);
}

function rotuloDoValor(campo: string, valor: unknown): string {
  if (campo === 'severidade' && typeof valor === 'string' && valor in ROTULO_SEVERIDADE) {
    return ROTULO_SEVERIDADE[valor as keyof typeof ROTULO_SEVERIDADE];
  }
  const bruto = texto(valor);
  // Descrição é longa: na linha do tempo basta o começo.
  return campo === 'descricao' && bruto.length > 80 ? `${bruto.slice(0, 77)}…` : bruto;
}

function rotuloStatus(valor: unknown): string {
  return typeof valor === 'string' && valor in ROTULO_STATUS_INCIDENTE
    ? ROTULO_STATUS_INCIDENTE[valor as keyof typeof ROTULO_STATUS_INCIDENTE]
    : texto(valor);
}

function objeto(valor: unknown): Record<string, unknown> {
  return valor && typeof valor === 'object' && !Array.isArray(valor) ? (valor as Record<string, unknown>) : {};
}

export function descreverEventoIncidente(evento: EventoIncidente): EventoDescrito {
  const d = evento.detalhe;
  switch (evento.tipo) {
    case 'criado': {
      const versoes = Array.isArray(d.versoes) ? d.versoes.filter((v): v is string => typeof v === 'string') : [];
      const severidade = typeof d.severidade === 'string' ? rotuloDoValor('severidade', d.severidade).toLowerCase() : null;
      return {
        resumo: severidade ? `Incidente registrado (severidade ${severidade})` : 'Incidente registrado',
        mudancas: [],
        complemento: versoes.length > 0 ? `Versões: ${versoes.join(', ')}` : null,
      };
    }
    case 'editado': {
      const mudancas = Object.entries(d).map(([campo, valor]) => {
        const par = objeto(valor);
        return { campo: ROTULO_CAMPO[campo] ?? campo, de: rotuloDoValor(campo, par.de), para: rotuloDoValor(campo, par.para) };
      });
      return {
        resumo: mudancas.length === 1 ? `Editado: ${mudancas[0].campo.toLowerCase()}` : 'Incidente editado',
        mudancas,
        complemento: null,
      };
    }
    case 'status':
      return { resumo: `Estado: ${rotuloStatus(d.de)} → ${rotuloStatus(d.para)}`, mudancas: [], complemento: null };
    case 'versao_adicionada':
      return { resumo: `Versão ${texto(d.versao)} adicionada`, mudancas: [], complemento: null };
    case 'versao_removida':
      return { resumo: `Versão ${texto(d.versao)} removida`, mudancas: [], complemento: null };
    default:
      return { resumo: 'Incidente atualizado', mudancas: [], complemento: null };
  }
}
